import { Box, Card, styled, Button, TextField, MenuItem, Typography, CircularProgress, alpha } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState, useCallback, useMemo } from "react";
import EnquiryFormTable from "page-sections/EnquiryForm/EnquiryFormTable";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { GetApp } from "@mui/icons-material";
import EnquiryFormExportModal from "page-sections/EnquiryForm/EnquiryFormExportModal";

const EnquiryForm = () => {
    const { setPageTitle } = usePageTitle();
    const [enquiryForms, setEnquiryForms] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [companyFilter, setCompanyFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [totalEnquiries, setTotalEnquiries] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page_number: null,
        previous_page_number: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [10, 25, 50, 100];

    // Check if user is Company type
    const isCompanyUser = apiService.isCompanyUser();
    const companyId = apiService.getCompanyId();

    useEffect(() => {
        setPageTitle('Enquiry Form');
    }, [setPageTitle]);

    // Fetch companies (for admin users only)
    const fetchCompanies = useCallback(async () => {
        if (isCompanyUser) {
            // For company users, use their own company
            const details = apiService.getCompanyDetails();
            const id = apiService.getCompanyId();
            const singleCompany = {
                id: id,
                name: details?.name || "My Company",
            };
            setCompanyOptions([singleCompany]);
            setCompanyFilter(id?.toString() || "");
            return;
        }

        setLoadingCompanies(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
            const options = response?.data?.Company || response?.data?.choices || response?.data || [];
            if (Array.isArray(options)) {
                setCompanyOptions(options);
            } else {
                setCompanyOptions([]);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanyOptions([]);
        } finally {
            setLoadingCompanies(false);
        }
    }, [isCompanyUser]);

    // Fetch categories from API based on selected company
    const fetchCategories = useCallback(async (selectedCompanyId = null) => {
        setLoadingCategories(true);
        try {
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            
            if (!targetCompanyId) {
                setCategories([]);
                return;
            }

            // Fetch categories for the selected company
            const userId = localStorage.getItem('user_id');
            const response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${targetCompanyId}&user_id=${userId}`);
            
            if (response?.success) {
                const categoryList = response.data?.ProductCategory || [];
                setCategories(categoryList);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    }, [isCompanyUser, companyId]);

    // Fetch enquiry forms with company_id filter and pagination
    const fetchEnquiryForms = useCallback(async (selectedCompanyId = null, page = 1, pageSizeParam = 10) => {
        setLoading(true);
        try {
            let url = API_ENDPOINTS.GET_enquiry_form;
            const targetCompanyId = selectedCompanyId || (isCompanyUser ? companyId : null);
            const params = new URLSearchParams();
            params.set('page', String(page));
            params.set('page_size', String(pageSizeParam));
            if (targetCompanyId) {
                params.set('company_id', targetCompanyId);
            }
            url += (url.includes('?') ? '&' : '?') + params.toString();

            const response = await apiService.get(url);

            if (response?.success && response?.data?.EnquiryForm) {
                setEnquiryForms(response.data.EnquiryForm);
            } else {
                setEnquiryForms([]);
            }
            const paginationData = response?.pagination || response?.data?.pagination || {};
            if (Object.keys(paginationData).length > 0) {
                setPagination({
                    current_page: paginationData.current_page ?? page,
                    page_size: paginationData.page_size ?? pageSizeParam,
                    total_items: paginationData.total_items ?? 0,
                    total_pages: paginationData.total_pages ?? 1,
                    has_next: paginationData.has_next ?? false,
                    has_previous: paginationData.has_previous ?? false,
                    next_page_number: paginationData.next_page_number ?? null,
                    previous_page_number: paginationData.previous_page_number ?? null,
                });
                setCurrentPage(paginationData.current_page ?? page);
                setPageSize(paginationData.page_size ?? pageSizeParam);
            } else {
                const list = response?.data?.EnquiryForm || [];
                setPagination(prev => ({
                    ...prev,
                    total_items: list.length,
                    total_pages: 1,
                    current_page: 1,
                    page_size: pageSizeParam,
                }));
            }
        } catch (error) {
            console.error("API error:", error);
            setEnquiryForms([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyUser, companyId]);

    const handleEnquiryPageChange = (_, value) => {
        setCurrentPage(value);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, value, pageSize);
    };

    const handleEnquiryPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setCurrentPage(1);
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, 1, newSize);
    };

    // Fetch dashboard data for total enquiries
    const fetchDashboardData = useCallback(async () => {
        setLoadingDashboard(true);
        try {
            const response = await apiService.get(API_ENDPOINTS.GET_farmer_enquiry_dashboard);
            if (response?.success && response?.data?.summary) {
                setTotalEnquiries(response.data.summary.total_enquiries);
            } else {
                setTotalEnquiries(null);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setTotalEnquiries(null);
        } finally {
            setLoadingDashboard(false);
        }
    }, []);

    // Fetch companies on component mount
    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Fetch categories when company changes
    useEffect(() => {
        if (companyFilter || (isCompanyUser && companyId)) {
            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
            fetchCategories(targetCompanyId);
            // Reset category filter when company changes
            setCategoryFilter("");
        } else {
            setCategories([]);
            setCategoryFilter("");
        }
    }, [companyFilter, isCompanyUser, companyId, fetchCategories]);

    // Fetch enquiry forms on component mount and when company changes
    useEffect(() => {
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        fetchEnquiryForms(targetCompanyId, currentPage, pageSize);
    }, [companyFilter, isCompanyUser, companyId, fetchEnquiryForms]);

    // Fetch dashboard data on component mount
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Handle company filter change
    const handleCompanyFilterChange = (e) => {
        setCompanyFilter(e.target.value);
    };

    // Handle category filter change
    const handleCategoryFilterChange = (e) => {
        setCategoryFilter(e.target.value);
        // When category is selected, refresh with company_id
        const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
        if (targetCompanyId) {
            fetchEnquiryForms(targetCompanyId, currentPage, pageSize);
        }
    };

    // Export handlers
    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleExportModalClose = () => {
        setExportModalOpen(false);
    };

    return (
        <Box pt={2} pb={4}>
            <FlexBetween flexWrap="wrap" mb={2}>
                <FlexBox alignItems="center" gap={2} style={{ flexGrow: 1 }}>
                    <SearchInput
                        bordered={false}
                        placeholder="Search by farmer name, village, reference, product, SKU..."
                        onChange={e => setSearchValue(e.target.value)}
                        value={searchValue}
                        sx={{
                                        width: "45%",
                                        maxWidth: "none !important"
                                    }}
                    />
                </FlexBox>
                <Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={handleExportClick}
                        sx={{ textTransform: 'none' }}
                    >
                        Export to Excel
                    </Button>
                </Box>
            </FlexBetween>

            {/* Filter by Company (admin users only) */}
            {!isCompanyUser && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Filter by category:
                    </Typography>

                    <TextField
                        select
                        size="small"
                        label="All Category"
                        value={companyFilter}
                        onChange={handleCompanyFilterChange}
                        disabled={loading || loadingCompanies}
                        sx={{ minWidth: { xs: "100%", sm: 220 } }}
                    >
                        <MenuItem value="">All Company</MenuItem>
                        {loadingCompanies ? (
                            <MenuItem disabled>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                Loading companies...
                            </MenuItem>
                        ) : (
                            companyOptions.map((company) => (
                                <MenuItem key={company.id} value={company.id}>
                                    {company.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    {/* Total Enquiries Display - Next to filter */}
                    {totalEnquiries !== null && (
                        <Box
                        sx={{
                        minWidth: { xs: "100%", sm: 220 },
                        height: "40px",
                        display: "flex",
                        alignItems: "center",     // vertical center
                        // justifyContent: "center", // horizontal center
                        paddingLeft:  "10px",
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                        borderRadius: 1,
                        border: (theme) =>
                            `1px solid ${
                            theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.23)"
                                : "rgba(0, 0, 0, 0.23)"
                            }`,
                        }}
                    >
                           <Typography
                        variant="body1"
                        // fontWeight={600}
                        color="text.primary"
                        >
                                Total Enquiries:  {totalEnquiries.toLocaleString()}
                            </Typography>
                            {/* {loadingDashboard ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '24px' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            ) : (
                                <Typography 
                                    variant="body2" 
                                    fontWeight={700} 
                                    color="primary.main" 
                                    sx={{ 
                                        lineHeight: 1.4375,
                                        fontSize: "0.9375rem",
                                        letterSpacing: "0.01em",
                                    }}
                                >
                                    {totalEnquiries.toLocaleString()}
                                </Typography>
                            )} */}
                        </Box>
                    )}
                </Box>
            )}

            {/* Filter by Category */}
            {/* <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Typography variant="body2" fontWeight={600}>
                    Filter by category:
                </Typography>

                <TextField
                    select
                    size="small"
                    label="All Category"
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    disabled={loading || loadingCategories || (!companyFilter && !isCompanyUser)}
                    sx={{ minWidth: { xs: "100%", sm: 220 } }}
                >
                    <MenuItem value="">All Category</MenuItem>
                    {loadingCategories ? (
                        <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading categories...
                        </MenuItem>
                    ) : (
                        categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))
                    )}
                </TextField>
            </Box> */}

            {/* Total Enquiries Display - For company users (when filter is not shown) */}
            {isCompanyUser && totalEnquiries !== null && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Filter by category:
                    </Typography>
                    <Box
                        sx={{
                            position: "relative",
                            minWidth: { xs: "100%", sm: 220 },
                            minHeight: "40px", // Match TextField size="small" minimum height
                            height: "56px", // Match TextField size="small" with label height
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            px: 1.5, // Match TextField padding
                            pb: 1.25, // Match TextField bottom padding
                            pt: 2, // Space for label
                            backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                ? alpha(theme.palette.primary.main, 0.08)
                                : alpha(theme.palette.primary.main, 0.04),
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.mode === 'dark' 
                                ? alpha(theme.palette.primary.main, 0.3)
                                : alpha(theme.palette.primary.main, 0.2)}`,
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 1px 3px rgba(0, 0, 0, 0.2)'
                                : '0 1px 3px rgba(0, 0, 0, 0.08)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 2px 6px rgba(0, 0, 0, 0.3)'
                                    : '0 2px 6px rgba(0, 0, 0, 0.12)',
                            },
                        }}
                    >
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                                position: "absolute",
                                top: "9px",
                                left: "14px",
                                fontSize: "0.75rem",
                                lineHeight: 1,
                                fontWeight: 500,
                                letterSpacing: "0.02em",
                            }}
                        >
                            Total Enquiries
                        </Typography>
                        {loadingDashboard ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '24px' }}>
                                <CircularProgress size={16} />
                            </Box>
                        ) : (
                            <Typography 
                                variant="body2" 
                                fontWeight={700} 
                                color="primary.main" 
                                sx={{ 
                                    lineHeight: 1.4375,
                                    fontSize: "0.9375rem",
                                    letterSpacing: "0.01em",
                                }}
                            >
                                {totalEnquiries.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            <Card>
                <Box sx={{ mt: 1 }}>
                    <EnquiryFormTable
                        enquiryForms={enquiryForms}
                        searchQuery={searchValue}
                        loading={loading}
                        onRefresh={() => {
                            const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                            fetchEnquiryForms(targetCompanyId, currentPage, pageSize);
                            fetchDashboardData(); // Refresh dashboard data on manual refresh
                        }}
                        pagination={pagination}
                        onPageChange={handleEnquiryPageChange}
                        onPageSizeChange={handleEnquiryPageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                    />
                </Box>
            </Card>

            <EnquiryFormExportModal
                open={exportModalOpen}
                onClose={handleExportModalClose}
                onRefresh={() => {
                    const targetCompanyId = companyFilter || (isCompanyUser ? companyId : null);
                    fetchEnquiryForms(targetCompanyId, currentPage, pageSize);
                }}
            />
        </Box>
    );
};

export default EnquiryForm;

